import { IExtractedCertificateOfInsurance, IScope } from "@/api";
import { LegalDisclaimer } from "@/components/frontend/LegalDisclaimer";
import { SheetSnapPoints, SNAP_POINTS } from "@/config/constants";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useEffect, useRef, useState } from "react";
import { Sheet, SheetRef } from "react-modal-sheet";
import { FoundToggleState, ScopeDetailsTab } from "../../types";
import { Fields } from "../fields/Fields";
import { ScopeTopicsV2 } from "../scope-topics/ScopeTopicsV2";
import { FoundToggle } from "./FoundToggle";
import { ScopeDetailsNavigation } from "./ScopeDetailsNavigation";
import { SearchBar } from "./SearchBar";

interface ScopeDetailsProps {
    tab: ScopeDetailsTab;
    setTab: (tab: ScopeDetailsTab) => void;
    fieldsFoundToggle: FoundToggleState;
    setFieldsFoundToggle: (toggle: FoundToggleState) => void;
    clausesFoundToggle: FoundToggleState;
    setClausesFoundToggle: (toggle: FoundToggleState) => void;

    setFilteredClausesCount: (count: number) => void;

    search: string;
    setSearch: (search: string) => void;

    scope: IScope;
    coi?: IExtractedCertificateOfInsurance | null;

    pageNavigationPluginInstance: PageNavigationPlugin;
    pdfViewerLoaded: boolean;

    expandedFoundClause: string | false;
    setExpandedFoundClause: (expanded: string | false) => void;

    scrollToTopicId?: number | null;

    currentPage: number;
    totalPages: number;
}

export const ScopeDetails = ({
    tab,
    setTab,
    fieldsFoundToggle,
    setFieldsFoundToggle,
    clausesFoundToggle,
    setClausesFoundToggle,
    setFilteredClausesCount,
    search,
    setSearch,
    scope,
    coi,
    pageNavigationPluginInstance,
    pdfViewerLoaded,
    expandedFoundClause,
    setExpandedFoundClause,
    scrollToTopicId,
    currentPage,
    totalPages,
}: ScopeDetailsProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // sheet init
    const [sheetOpen, setSheetOpen] = useState(false);
    const sheetRef = useRef<SheetRef | undefined>(undefined);

    const renderTab = () => {
        return (
            <>
                <Box
                    sx={{
                        display:
                            tab === ScopeDetailsTab.FIELDS ? "block" : "none",
                    }}
                >
                    <Fields
                        scope={scope}
                        search={search}
                        foundToggle={fieldsFoundToggle}
                        pageNavigationPluginInstance={
                            pageNavigationPluginInstance
                        }
                        coi={coi}
                        currentPage={currentPage}
                        totalPages={totalPages}
                    />
                </Box>
                <Box
                    sx={{
                        display:
                            tab === ScopeDetailsTab.CLAUSES ? "block" : "none",
                    }}
                >
                    <ScopeTopicsV2
                        scope={scope}
                        search={search}
                        foundToggle={clausesFoundToggle}
                        setFilteredClausesCount={setFilteredClausesCount}
                        pageNavigationPluginInstance={
                            pageNavigationPluginInstance
                        }
                        pdfViewerLoaded={pdfViewerLoaded}
                        bottomSheetRef={sheetRef}
                        highlightedScopeClauseId={
                            expandedFoundClause
                                ? parseInt(
                                      expandedFoundClause
                                          .toString()
                                          .replace("panel", ""),
                                      10
                                  )
                                : null
                        }
                        onClauseSelect={(scopeClauseId) =>
                            setExpandedFoundClause(
                                scopeClauseId ? `panel${scopeClauseId}` : false
                            )
                        }
                        scrollToTopicId={scrollToTopicId}
                    />
                </Box>
            </>
        );
    };

    // controls the mobile sheet and ensures it cannot be closed
    useEffect(() => {
        if (isMobile) {
            !sheetOpen && setSheetOpen(true);
        } else {
            setSheetOpen(false);
        }
    }, [isMobile, setSheetOpen, sheetOpen]);

    if (isMobile) {
        return (
            <Sheet
                ref={sheetRef}
                isOpen={sheetOpen}
                snapPoints={SNAP_POINTS}
                initialSnap={SNAP_POINTS.indexOf(SheetSnapPoints.DEFAULT)}
                onClose={() => {
                    setSheetOpen(false);
                }}
                onCloseEnd={() => (isMobile ? setSheetOpen(true) : undefined)}
                dragCloseThreshold={1}
                dragVelocityThreshold={5000}
                style={{ zIndex: 999 }}
            >
                <Sheet.Container>
                    <Sheet.Header disableDrag={false} />
                    <Sheet.Content
                        style={{ paddingBottom: sheetRef.current?.y }}
                    >
                        <ScopeDetailsNavigation
                            tab={tab}
                            setTab={setTab}
                            fieldsEnabled={scope.field_extraction_enabled}
                        />
                        <FoundToggle
                            foundToggle={
                                tab === ScopeDetailsTab.FIELDS
                                    ? fieldsFoundToggle
                                    : clausesFoundToggle
                            }
                            setFoundToggle={
                                tab === ScopeDetailsTab.FIELDS
                                    ? setFieldsFoundToggle
                                    : setClausesFoundToggle
                            }
                        />
                        <Box sx={{ overflow: "auto", flex: 1 }}>
                            {renderTab()}

                            {/* Legal Disclaimer */}
                            <LegalDisclaimer />
                        </Box>
                    </Sheet.Content>
                </Sheet.Container>
            </Sheet>
        );
    }

    return (
        <Stack
            width={"100%"}
            maxWidth={450}
            height={"100%"}
            display={isMobile ? "none" : "flex"}
        >
            <Box
                sx={{
                    bgcolor: "background.paper",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {!isMobile && (
                    <Box sx={{ borderBottom: 1, borderColor: "divider", p: 2 }}>
                        <SearchBar search={search} setSearch={setSearch} />
                    </Box>
                )}
                <ScopeDetailsNavigation
                    tab={tab}
                    setTab={setTab}
                    fieldsEnabled={scope.field_extraction_enabled}
                />
                <FoundToggle
                    foundToggle={
                        tab === ScopeDetailsTab.FIELDS
                            ? fieldsFoundToggle
                            : clausesFoundToggle
                    }
                    setFoundToggle={
                        tab === ScopeDetailsTab.FIELDS
                            ? setFieldsFoundToggle
                            : setClausesFoundToggle
                    }
                />

                <Box
                    sx={{
                        overflow: "auto",
                        flex: 1,
                        py: 1,
                        "&::-webkit-scrollbar": {
                            width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(0,0,0,0.2)",
                            borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "rgba(0,0,0,0.3)",
                        },
                    }}
                >
                    {renderTab()}

                    {/* Legal Disclaimer */}
                    <LegalDisclaimer />
                </Box>
            </Box>
        </Stack>
    );
};
